import React, { Fragment, useState, useEffect } from "react";
import { usePriceBlocsContext } from "@priceblocs/react-priceblocs-js";
import Overlay from "components/Overlay";
import PreviewInvoice from "../PreviewInvoice";
import { classNames } from "utils/ui";
import { crispShow, crispHide } from "utils/crisp";
import useCheckoutCart from "utils/hooks/useCheckoutCart";
import Button from "components/Form/fields/Button";

const DEFAULT_COPY = {
  value: "Buy now",
  loading: "Loading..."
};

const PREVIEW_INVOICE = "preview_invoice";
const OVERLAY_TYPE = {
  PREVIEW_INVOICE
};

const OVERLAY_TITLE_MAP = {
  [OVERLAY_TYPE.PREVIEW_INVOICE]: "Update subscription"
};

const CheckoutButton = ({
  price,
  product,
  theme,
  checkout: checkoutInput,
  copy: copyProps
}) => {
  const [overlay, setOverlay] = useState(null);

  const {
    action,
    checkout: cartCheckout,
    subscription,
    disabled
  } = useCheckoutCart({
    price,
    product,
    checkout: checkoutInput
  });
  const showOverlay = Boolean(overlay);
  useEffect(() => {
    showOverlay ? crispHide() : crispShow();
  }, [showOverlay]);

  const { refetch, checkout } = usePriceBlocsContext();
  /**
   * Hierarchy of copy values
   * - Props
   * - Inferred
   * - Defaults
   */
  const copy = { ...DEFAULT_COPY, ...action, ...copyProps };

  const [loading, setLoading] = useState(false);
  const buttonCopy = loading ? copy.loading : copy.value;

  const buttonClasses = classNames(
    `flex shadow-0 justify-center items-center text-center mt-auto text-white border-0 pv2 ph4 w-100 focus:outline-none rounded-md transition duration-150 ease-in-out disabled:opacity-20`,
    !disabled ? `hover:bg-blue` : "",
    disabled ? "cursor-not-allowed" : ""
  );

  const buttonProps = {
    type: "button",
    disabled: loading || disabled,
    className: buttonClasses,
    copy: buttonCopy,
    loading,
    theme
  };
  /**
   * If theres an active subscription
   * - we need to preview the invoice change based on the selected prices
   * Else
   * - proceeed directly to checkout
   */
  if (subscription) {
    buttonProps.onClick = () => setOverlay(OVERLAY_TYPE.PREVIEW_INVOICE);
  } else {
    buttonProps.onClick = async () => {
      setLoading(true);
      await checkout(cartCheckout);
      setLoading(false);
    };
  }

  return (
    <Fragment>
      <Button {...buttonProps} />
      {showOverlay && (
        <Overlay hideOverlay={() => setOverlay(false)}>
          <PreviewInvoice
            title={OVERLAY_TITLE_MAP[overlay]}
            setOpen={setOverlay}
            subscription={subscription}
            prices={cartCheckout.prices}
            afterConfirm={() => {
              setOverlay(null);
              refetch();
            }}
          />
        </Overlay>
      )}
    </Fragment>
  );
};

export default CheckoutButton;
